import type { FC } from 'react'
import { Typography, Button, Tooltip, Stack, IconButton, Box } from '@mui/material'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { gridCellClasses, gridHeaderClasses } from '@/components/grid/DataGrid'
import type { GridCellCtx, GridColumn } from '@/components/grid/types'
import { fetchStationDecoderPurposeIds } from '@/utils/stationDecoderPurposes'
import {
  COLUMN_CONFIG,
  COLUMN_GROUPS,
  STATION_TYPE_OPTIONS,
} from './column-config'
import SearchableSelectCellEditor from './searchable-select-cell-editor'
import TextContainsFilter from './text-contains-filter'
import StateBucketFilter from './state-bucket-filter'
import { pipeDepthByName } from './pipe-dropdown-order'
import {
  applyUnitConversion,
  withUnitLabel,
  type UnitConverters,
} from '@/components/grid/unitColumn'
import { missingStationFields } from './station-completeness'
import classes from './styles.module.scss'

export type StationRow = Record<string, any>

// Blend a #RRGGBB hex with an alpha into an rgba() string — used for the soft
// tinted backgrounds/borders on status pills and chips.

// For non-irrigation stations only these columns are editable; everything else
// is locked (the irrigation-specific fields don't apply).
export const NON_IRRIGATION_EDITABLE_FIELDS = [
  // A name is a name — nothing about it is irrigation-specific.
  'description',
  'decoderAddress',
  'stationType',
  'stationPhoto',
  'area',
  'subArea',
]

// Fields that can be flagged as required on save — each needs the cell style
// that renders the red highlight from the page's highlight map, and each header
// carries the dot that says so before the user finds out by saving.
const MANDATORY_HIGHLIGHT_FIELDS = [
  'description',
  'decoderAddress',
  // Required like the rest: it is defaulted from the decoder's purpose, but a
  // station cannot be written without one, and the header should say so.
  'stationType',
  'area',
  'subArea',
  'plant',
  'soil',
  'emitter',
  'pipe',
  'expectedFlow',
  'precipitationRate',
  'priority',
]

/** Whether this cell holds a staged value that has not been written yet. */
export const hasPendingEditFor = (
  pendingEdits: Map<string, Record<string, unknown>> | undefined,
  rowId: string,
  field: string,
): boolean => {
  const edit = pendingEdits?.get(rowId)
  return edit != null && field in edit
}

export interface BuildColumnsParams {
  bulkEditMode: boolean
  /**
   * The controller the grid is showing. Draft rows carry no `interfaceId` of
   * their own, and reading one off the row sent `/api/Controller/NaN`
   * (SCRUM-1032 round 7, item 12).
   */
  interfaceId?: number | string | null
  /**
   * Guided setup. A station there is created by the step itself and removed by
   * dropping its row before it is written, so the per-row DELETE — which hits
   * the API on a record the user has only just made — is left to /config.
   */
  guidedSetup?: boolean
  /**
   * Saved stations whose decoder is no longer a station decoder. These keep
   * their DELETE in the wizard: it is the only way out of the conflict, and
   * the row is flagged as one.
   */
  conflictingStationIds?: Set<string>
  /**
   * Decoder addresses whose station has been written this sitting. Their cells
   * drop the amber for the edited tint as each row lands, so a batch save
   * reads as progress rather than as one long wait. Addressed by decoder, not
   * by row id: a draft's id does not survive a rebuild.
   */
  savedAddresses?: Set<string>
  /**
   * SCRUM-1032 QC item 15a — why a flagged row is flagged, in words.
   *
   * A red row used to say only that something was wrong with it, and Wei Woon
   * read that as "these cells need fixing" — reasonably, since red on a cell
   * means exactly that elsewhere. But the things that flag a station row are
   * not cell problems: it shares a decoder with another row, or the server
   * refused it. Every cell really was filled in. The row now carries an icon
   * saying which it is, so there is something to act on rather than a colour
   * to puzzle over.
   */
  issueReason?: (row: StationRow) => string | null
  isDark: boolean
  t: (key: string, options?: Record<string, any>) => string
  areaOptionsData: any[]
  subAreaOptionsData: any[]
  plantOptionsData: any[]
  soilOptionsData: any[]
  emitterOptionsData: any[]
  pipeOptionsData: any[]
  etZoneOptionsData: any[]
  /**
   * Unit display for the columns that hold a physical quantity. Values are
   * stored — and sent — in metric; these turn them into what the user reads.
   */
  units: UnitConverters
  /** A control hub has sub-controllers worth naming in the decoder picker. */
  showController?: boolean
  /** Set once Save has run, so the area/sub-area pairing is only flagged then. */
  submitValidatedRef: React.MutableRefObject<boolean>
  /**
   * The rows as displayed (base ⊕ pending edits), read live by the decoder
   * editor so an address another row has just taken is no longer offered.
   */
  getMergedRows: () => StationRow[]
  /** Red "required" fill per row and field, set by a failed save. */
  cellHighlights: Record<string, Record<string, string>>
  onViewItem?: (id: string) => void
  onDeleteItem?: (id: string, name?: string) => void
  onSaveRow?: (row: StationRow) => void
  onDiscardRow?: (id: string) => void
  isNonIrrigation: (row: any) => boolean
  /**
   * Renderers for the columns whose cell is a control rather than a value.
   * They are wired by field, so each one draws only while its column is part
   * of COLUMN_CONFIG — the same arrangement the ag component map had.
   */
  controlRenderers?: Partial<
    Record<'stationPhoto' | 'gpsCoordinates' | 'delete', FC<GridCellCtx<StationRow>>>
  >
}

const groupOf: Record<string, (typeof COLUMN_GROUPS)[number]> = {}
COLUMN_GROUPS.forEach((group) =>
  group.children.forEach((field) => {
    groupOf[field] = group
  }),
)

const dedupe = (values: any[]) =>
  [...new Set(values.filter((value) => value != null && value !== ''))].sort()

/** The label a dropdown column shows and filters on. */
const subAreaName = (row: StationRow) =>
  typeof row?.subArea === 'object' ? (row.subArea?.name ?? '') : (row?.subArea ?? '')

const pipeName = (row: StationRow) =>
  typeof row?.pipe === 'object' ? (row.pipe?.description ?? '') : (row?.pipe ?? '')

const etZoneNameFor = (row: StationRow, etZoneOptionsData: any[]) => {
  const value = row?.etZone
  const id = typeof value === 'object' && value ? value.id : row?.etZoneId
  const fromOptions =
    id != null ? etZoneOptionsData.find((option) => option.id === id)?.name : null
  return fromOptions || (typeof value === 'object' ? value?.name : value) || ''
}

const runTimeMinutes = (row: StationRow) => {
  const raw = String(row?.defaultRunTime ?? '')
  const [h, m, s] = raw.split(':').map((part: string) => parseInt(part) || 0)
  return (h || 0) * 60 + (m || 0) + Math.round((s || 0) / 60)
}

const PlainValueCell = ({ value }: GridCellCtx<StationRow>) => (
  <span className={classes.plainValue}>{(value as string) || '-'}</span>
)

export const buildColumns = (
  params: BuildColumnsParams,
): GridColumn<StationRow>[] => {
  const {
    bulkEditMode,
    isDark,
    t,
    units,
    submitValidatedRef,
    cellHighlights,
    onViewItem,
    onDeleteItem,
    onSaveRow,
    onDiscardRow,
    isNonIrrigation,
    guidedSetup = false,
    conflictingStationIds,
    savedAddresses,
    issueReason,
  } = params

  const columns = COLUMN_CONFIG.map((config: any): GridColumn<StationRow> => {
    const group = groupOf[config.field]

    const column: GridColumn<StationRow> = {
      field: config.field,
      headerName: config.headerName
        ? config.metricUnit
          ? withUnitLabel(
              t(config.headerName),
              units.getImperialUnitLabel(config.metricUnit),
            )
          : t(config.headerName)
        : '',
      sortable: config.sortable,
      width: config.width,
      minWidth: config.minWidth,
      maxWidth: config.maxWidth,
      // Every data column flexes and falls back to its minimum, the way the
      // ag defaultColDef (flex 1 / minWidth) laid the grid out.
      flex: config.maxWidth ? 0 : 1,
      pinned: config.pinned,
      lockPosition: config.lockPosition ? true : undefined,
      resizable: config.suppressMovable ? false : undefined,
      group: group?.groupId,
      // Numbers stay right-aligned in their cells, but every header reads from
      // the left: on a grid this wide, half the labels starting left and half
      // right looked like a mistake rather than a convention. The dot marks a
      // column the save will not accept as blank.
      headerClass: gridHeaderClasses.textLeft,
      required: MANDATORY_HIGHLIGHT_FIELDS.includes(config.field),
      headerTooltip: config.headerTooltip ? t(config.headerTooltip) : undefined,
      editable: ({ row }) => {
        if (!bulkEditMode) return false
        if (isNonIrrigation(row)) {
          return NON_IRRIGATION_EDITABLE_FIELDS.includes(config.field)
        }
        return Boolean(config.editable)
      },
      cellClass: (ctx) => {
        const list: string[] = []

        // Declared before the rest so the class order is stable; the stylesheet
        // is what decides it beats `.cellChanged`. Not on a cell holding an
        // edit made since: that value is waiting to be written, and the green
        // would hide the amber that says so (round 7, item 5).
        const editPending = hasPendingEditFor(
          ctx.api?.getPendingEdits?.(),
          ctx.rowId,
          config.field,
        )
        if (
          !editPending &&
          savedAddresses?.has(String(ctx.row?.decoderAddress ?? ''))
        ) {
          list.push(gridCellClasses.saved)
        }

        // A required value still missing is marked on the cell itself, not
        // only counted in the footer (round 7, item 7). `ctx.row` already
        // carries the staged edits.
        if (
          bulkEditMode &&
          MANDATORY_HIGHLIGHT_FIELDS.includes(config.field) &&
          missingStationFields(ctx.row).includes(config.field)
        ) {
          list.push(classes.cellMissing)
        }

        if (bulkEditMode) {
          if (!config.editable) {
            list.push(gridCellClasses.nonEditable)
          } else {
            const cellEditable = isNonIrrigation(ctx.row)
              ? NON_IRRIGATION_EDITABLE_FIELDS.includes(config.field)
              : true
            if (cellEditable) {
              list.push(gridCellClasses.editable)
              // Dropdown affordance (chevron) for the select columns.
              if (config.valueType === 'richSelect') list.push(classes.dropdownCell)
            } else {
              list.push(gridCellClasses.nonEditable)
            }
          }
        }

        // An area without its sub-area (and, once Save has run, the reverse)
        // is the one pairing the grid flags before the server does.
        if (config.field === 'subArea') {
          const hasArea = ctx.row?.area && ctx.row.area !== ''
          if (hasArea && !subAreaName(ctx.row)) list.push(classes.cellRequired)
        }
        // A stored value the editor would now refuse (a priority saved above
        // 255 before the limit) is flagged so it gets corrected.
        if (config.numberRange && isOutsideRange(ctx.value, config.numberRange)) {
          list.push(gridCellClasses.invalid)
        }
        if (config.field === 'area' && submitValidatedRef.current) {
          const hasArea = ctx.value && ctx.value !== ''
          if (!hasArea && subAreaName(ctx.row)) list.push(classes.cellRequired)
        }

        return list
      },
    }

    if (MANDATORY_HIGHLIGHT_FIELDS.includes(config.field)) {
      column.cellStyle = ({ rowId }) => {
        const color = cellHighlights[rowId]?.[config.field]
        if (!color) return undefined
        return {
          backgroundColor: color,
          borderColor: 'red',
          borderWidth: '0.5px',
          color: '#000',
        }
      }
    }

    if (config.editable) setupEditor(column, config, params)
    setupRenderer(column, config, params)
    setupFilter(column, config, params)

    if (config.metricUnit) {
      applyUnitConversion(column, config.field, config.metricUnit, units)
    }

    // Non-irrigation stations have no data for the irrigation-only columns, so
    // render those cells blank for them. Display-only: the underlying data,
    // filters and sorting are untouched.
    const isIrrigationOnly =
      config.editable && !NON_IRRIGATION_EDITABLE_FIELDS.includes(config.field)
    if (isIrrigationOnly) {
      const inner = column.cellRenderer
      column.cellRenderer = (ctx) => {
        if (isNonIrrigation(ctx.row)) return null
        return inner ? inner(ctx) : <>{(ctx.value as string) ?? null}</>
      }
    }

    return column
  })

  // Mark the leaf columns at each group's edges so the header can draw the
  // divider that shows where a group starts and ends.
  COLUMN_GROUPS.forEach((group) => {
    const members = group.children
      .map((field) => columns.find((column) => column.field === field))
      .filter(Boolean) as GridColumn<StationRow>[]
    if (members.length === 0) return
    members[0].headerClass = [members[0].headerClass, classes.groupStart]
      .filter(Boolean)
      .join(' ')
    const last = members[members.length - 1]
    last.headerClass = [last.headerClass, classes.groupEnd].filter(Boolean).join(' ')
  })

  // --- Columns the config spec cannot describe -----------------------------

  const viewDetail = columns.find((column) => column.field === 'viewDetail')
  if (viewDetail) {
    viewDetail.cellRenderer = ({ row }) => {
      const id = row?.id
      if (id == null || Number(id) < 0) return null
      return (
        <Tooltip title={t('commonActionsViewDetails')}>
          <IconButton
            size="small"
            data-no-cell-edit
            onClick={() => onViewItem?.(String(id))}
            sx={{
              color: 'primary.main',
              padding: '5px',
              '&:hover': { backgroundColor: 'rgba(0, 71, 68, 0.08)' },
            }}
          >
            <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )
    }
    // Left status-accent rail: a thin coloured bar on the row's leading edge
    // that reflects the station state at a glance.
    viewDetail.cellStyle = ({ row }) => {
      const state = String(row?.state || '').toLowerCase()
      let accent = isDark ? '#3f5563' : '#CBD5E1'
      if (state.includes('error')) accent = isDark ? '#F87171' : '#EF4444'
      else if (state.includes('start') && state.includes('wait'))
        accent = isDark ? '#FBBF24' : '#F59E0B'
      else if (state.includes('start') || state.includes('running'))
        accent = isDark ? '#4ADE80' : '#16A34A'
      return { borderLeft: `3px solid ${accent}`, justifyContent: 'center', padding: 0 }
    }
  }

  const viewAction = columns.find((column) => column.field === 'viewAction')
  if (viewAction) {
    viewAction.cellRenderer = ({ row }) => {
      const id = row?.id
      const reason = issueReason?.(row)

      // Shown wherever the row sits, saved or draft: the reason is the whole
      // point of the flag, and a draft in the wizard otherwise renders nothing
      // here at all.
      const issueIcon = reason ? (
        <Tooltip title={reason} placement="left">
          <ErrorOutlineIcon
            sx={{ fontSize: 17, color: '#dc3545', flexShrink: 0 }}
          />
        </Tooltip>
      ) : null

      // New (unsaved) rows carry a negative temp id → a per-row Save plus a
      // local-discard "✕" instead of DELETE. Neither is offered in the wizard.
      //
      // Save, because the step writes each row the moment it is complete: a
      // button saying "save this one now" answers a question already answered,
      // and on a list of a thousand it was a column of noise.
      //
      // Discard, because these rows are not the user's to discard. They are
      // rebuilt from the decoders on every visit, so a dismissed row came
      // straight back on reload while the count quietly disagreed with the
      // list. A station draft exists because a decoder says it is a station;
      // the way to not have one is to change that decoder's purpose, which the
      // decoder step now asks about properly.
      if (Number(id) < 0) {
        if (guidedSetup) return issueIcon
        const isUntouchedPlaceholder =
          row?.__isEmptyPlaceholder && !row?.decoderAddress
        return (
          <Stack
            direction="row"
            spacing={1.5}
            data-no-cell-edit
            sx={{ height: '100%', alignItems: 'center' }}
          >
            <Button
              size="small"
              variant="primary"
              onClick={() => onSaveRow?.(row)}
              disabled={isUntouchedPlaceholder}
              sx={{ minWidth: 64, textTransform: 'none' }}
            >
              {t('commonActionsSave')}
            </Button>
            {!isUntouchedPlaceholder && (
              <Typography
                onClick={() => onDiscardRow?.(String(id))}
                sx={{
                  fontSize: '15px',
                  color: 'text.main',
                  cursor: 'pointer',
                  userSelect: 'none',
                  lineHeight: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#d32f2f' },
                }}
              >
                ✕
              </Typography>
            )}
          </Stack>
        )
      }

      // Normal row: DELETE only (this column is shown only in bulk-edit mode).
      if (!bulkEditMode) return null
      if (guidedSetup && !conflictingStationIds?.has(String(id))) return null
      return (
        <Stack
          direction="row"
          spacing={1.5}
          data-no-cell-edit
          sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}
        >
          <DeleteIconButton
            disabled={!onDeleteItem}
            isDark={isDark}
            onClick={() =>
              onDeleteItem?.(String(id), String(row?.description ?? ''))
            }
          />
        </Stack>
      )
    }
  }

  return columns
}

// --- Editors ---------------------------------------------------------------

const RICH_SELECT_COLUMNS: Record<
  string,
  (params: BuildColumnsParams) => string[]
> = {
  area: ({ areaOptionsData }) => areaOptionsData.map((item) => item.name),
  plant: ({ plantOptionsData }) => plantOptionsData.map((item) => item.description),
  soil: ({ soilOptionsData }) => soilOptionsData.map((item) => item.description),
  emitter: ({ emitterOptionsData }) => emitterOptionsData.map((item) => item.emitter),
  pipe: ({ pipeOptionsData }) => pipeOptionsData.map((item) => item.description),
  etZone: ({ etZoneOptionsData }) => etZoneOptionsData.map((item) => item.name),
}

/** Dropdown columns whose pick also stages the id the save payload sends. */
const ID_LOOKUPS: Record<
  string,
  (params: BuildColumnsParams) => {
    options: any[]
    labelField: string
    idField: string
  }
> = {
  plant: ({ plantOptionsData }) => ({
    options: plantOptionsData,
    labelField: 'description',
    idField: 'plantId',
  }),
  soil: ({ soilOptionsData }) => ({
    options: soilOptionsData,
    labelField: 'description',
    idField: 'soilId',
  }),
  emitter: ({ emitterOptionsData }) => ({
    options: emitterOptionsData,
    labelField: 'emitter',
    idField: 'emitterId',
  }),
}

const setupEditor = (
  column: GridColumn<StationRow>,
  config: any,
  params: BuildColumnsParams,
) => {
  switch (config.valueType) {
    case 'richSelect':
      column.editor = SearchableSelectCellEditor
      if (config.field === 'decoderAddress') {
        setupDecoderEditor(column, params)
      } else if (config.field === 'area') {
        column.editorParams = {
          values: params.areaOptionsData.map((item) => item.name),
        }
        // A new area invalidates the sub-area under it, so the pick clears it
        // and the sub-area editor reopens on the new area's list.
        column.valueSetter = (_row, value) => {
          const selected = params.areaOptionsData.find(
            (item) => item.name === value,
          )
          return {
            area: value,
            ...(selected ? { areaId: selected.id } : {}),
            subArea: '-',
            subAreaId: null,
          }
        }
      } else if (config.field === 'subArea') {
        setupSubAreaEditor(column, params)
      } else if (config.field === 'stationType') {
        setupStationTypeEditor(column, params)
      } else if (config.field === 'pipe') {
        // Already in hydraulic-tree order; each sub-pipe is indented under the
        // pipe it hangs off (QC round 2, item 5).
        const depths = pipeDepthByName(params.pipeOptionsData)
        column.editorParams = {
          values: params.pipeOptionsData.map((item) => item.description),
          renderOption: (option: string) => {
            const depth = depths.get(option) ?? 0
            return (
              <Box
                component="span"
                data-testid={`option-pipe-${option}`}
                data-depth={depth}
                sx={{
                  display: 'block',
                  pl: `${depth * 16}px`,
                  fontWeight: depth === 0 ? 600 : 400,
                  color: depth === 0 ? 'text.main' : 'grey.800',
                  lineHeight: '22px',
                }}
              >
                {depth > 0 && (
                  <Box component="span" sx={{ color: 'grey.600', mr: '6px' }}>
                    └
                  </Box>
                )}
                {option}
              </Box>
            )
          },
        }
        // The row keeps the whole pipe object: the save payload reads its id.
        column.valueSetter = (_row, value) => {
          const selected = params.pipeOptionsData.find(
            (item) => item.description === value,
          )
          return selected ? { pipe: selected, pipeId: selected.id } : { pipe: value }
        }
      } else if (config.field === 'etZone') {
        column.editorParams = {
          values: params.etZoneOptionsData.map((item) => item.name),
          // Micro climate is optional, so a zone picked by mistake has to be
          // removable. "None" commits '-', which the setter below clears.
          allowClear: true,
        }
        column.valueSetter = (_row, value) => {
          if (!value || value === '-') return { etZone: null, etZoneId: null }
          const selected = params.etZoneOptionsData.find(
            (item) => item.name === value,
          )
          return selected
            ? { etZone: selected, etZoneId: selected.id }
            : { etZone: value }
        }
      } else {
        const values = RICH_SELECT_COLUMNS[config.field]?.(params) ?? []
        column.editorParams = { values }
        // The row carries both the label and the id the save payload sends, so
        // one edit stages both — the grid's buffer is the only change record.
        const lookup = ID_LOOKUPS[config.field]
        if (lookup) {
          column.valueSetter = (_row, value) => {
            const { options, labelField, idField } = lookup(params)
            const selected = options.find((item) => item[labelField] === value)
            return selected
              ? { [config.field]: value, [idField]: selected.id }
              : { [config.field]: value }
          }
        }
      }
      break
    case 'number':
      column.editor = 'number'
      // Right-aligns the cell and the header to match the editor, which is
      // always right-aligned. Without this the figure sat left in the list and
      // jumped right the moment it was clicked.
      column.numeric = true
      if (config.numberRange) {
        column.editorParams = {
          min: config.numberRange.min,
          max: config.numberRange.max,
          outOfRangeMessage: config.numberRangeMessage
            ? params.t(config.numberRangeMessage)
            : undefined,
        }
      }
      break
    case 'runtime':
      // Stored as an "HH:MM:SS" string (the BE contract), shown and edited as
      // whole minutes. valueGetter/valueSetter translate between the two.
      column.editor = 'number'
      column.numeric = true
      column.editorParams = { min: 0, max: 1020 }
      column.valueGetter = runTimeMinutes
      column.valueSetter = (_row, value) => {
        const minutes = Math.max(0, Math.min(1020, Math.round(Number(value) || 0)))
        return {
          defaultRunTime: `${String(Math.floor(minutes / 60)).padStart(
            2,
            '0',
          )}:${String(minutes % 60).padStart(2, '0')}:00`,
        }
      }
      break
    default:
      column.editor = 'text'
      break
  }
}

const isOutsideRange = (value: unknown, range: { min: number; max: number }) => {
  if (value == null || value === '') return false
  const n = Number(value)
  return !Number.isNaN(n) && (n < range.min || n > range.max)
}

/** The decoder's identity as the row stores it, and the save sends it. */
const decoderRefOf = (option: any) => `${option.address}-${option.index}`

/**
 * The picker's option value.
 *
 * It carries every line's text, so typing the decoder's name, its address or
 * its controller all find the row — and, on a control hub, it is what keeps
 * the value UNIQUE. Two sub-controllers can hold the same address-index (this
 * dev hub has 37035-1 on both), so an option keyed on address alone is
 * ambiguous: the list would show two identical entries and the save would
 * resolve whichever came first, filing the station under the wrong controller.
 *
 * Same construction as the flow valve and flow sensor pickers.
 */
const decoderLabel = (option: any) =>
  [option.name, decoderRefOf(option), option.twiCustomName]
    .filter(Boolean)
    .join(' \u00b7 ')

const setupDecoderEditor = (
  column: GridColumn<StationRow>,
  {
    getMergedRows,
    showController,
    t,
    interfaceId: gridInterfaceId,
  }: BuildColumnsParams,
) => {
  // Options are fetched per row on open, so the objects behind the labels are
  // stashed here for the renderer and the setter to read back.
  const optionByLabel = new Map<string, any>()

  column.editorParams = ({ row }) => {
    const currentId = row?.id
    const interfaceId = Number(row?.interfaceId ?? gridInterfaceId)
    const currentAddress = row?.decoderAddress
    const currentTwiId = row?.twiId

    const fetchValues = async (): Promise<string[]> => {
      // No controller to ask: nothing to offer, and no request that can only
      // come back 403.
      if (!Number.isFinite(interfaceId)) return []
      const res: any = await controllerDataConfigApi.getAll({
        interfaceId,
        PageIndex: 1,
        PageSize: 0,
        Purpose: await fetchStationDecoderPurposeIds(Number(interfaceId)),
        type: 1,
        isRequiredStation: true, // only unassigned (+ this station's current)
        // A draft's id is a negative placeholder the server has never seen.
        stationId: Number(currentId) > 0 ? Number(currentId) : undefined,
      } as any)
      const list: any[] = res?.data?.data ?? res?.data ?? []
      const usable = list.filter((item) => item.address && item.index)

      // Drop decoders already picked on OTHER rows in this bulk-edit session
      // (unsaved), so two rows cannot be given the same one. Keyed on the
      // controller as well as the address — on a hub the same address on a
      // different sub-controller is a different decoder.
      const usedByOtherRows = new Set<string>()
      getMergedRows().forEach((other) => {
        const address = other?.decoderAddress
        if (other?.id !== currentId && address && address !== '-') {
          usedByOtherRows.add(`${address}@${other?.twiId ?? ''}`)
        }
      })

      const available = usable.filter((item) => {
        const key = `${decoderRefOf(item)}@${item.twiId ?? ''}`
        return (
          !usedByOtherRows.has(key) ||
          (decoderRefOf(item) === currentAddress &&
            (item.twiId ?? '') === (currentTwiId ?? ''))
        )
      })

      optionByLabel.clear()
      available.forEach((item) => optionByLabel.set(decoderLabel(item), item))

      const sorted = [...available].sort((a, b) =>
        a.address === b.address ? a.index - b.index : a.address - b.address,
      )
      return sorted.length > 0
        ? sorted.map(decoderLabel)
        : [t('stationsNoAvailableDecoder')]
    }

    /** Name, then address-index, then the controller — as the flow rule lists. */
    const renderOption = (label: string) => {
      const option = optionByLabel.get(label)
      if (!option) return label
      return (
        <Stack sx={{ gap: '2px', py: '2px' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {option.name || decoderRefOf(option)}
          </Box>
          {option.name ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {decoderRefOf(option)}
            </Box>
          ) : null}
          {showController && option.twiCustomName ? (
            <Box component="span" sx={{ opacity: 0.75 }}>
              {option.twiCustomName}
            </Box>
          ) : null}
        </Stack>
      )
    }

    return {
      // Static baseline while the async list loads (keeps the current value).
      values: currentAddress && currentAddress !== '-' ? [currentAddress] : [],
      fetchValues,
      renderOption,
      minWidth: 320,
      // The row stores the bare address, the options are composite — this is
      // what lets the editor tick the right one instead of listing the
      // current decoder twice.
      isInitial: (label: string) => {
        const option = optionByLabel.get(label)
        if (!option) return label === currentAddress
        return (
          decoderRefOf(option) === currentAddress &&
          (currentTwiId == null || option.twiId === currentTwiId)
        )
      },
    }
  }

  // The row keeps the bare "<address>-<index>" — that is what the cell shows
  // and what the save splits — plus the twiId, so the save no longer has to
  // guess the controller from an address that may not be unique.
  column.valueSetter = (row, value) => {
    const label = String(value ?? '')
    const option = optionByLabel.get(label)
    if (option) {
      return {
        decoderAddress: decoderRefOf(option),
        address: Number(option.address),
        index: Number(option.index),
        twiId: option.twiId ?? null,
        decoderId: option.id || undefined,
      }
    }
    // The untouched baseline value, or "No available decoder".
    if (!label.includes('-'))
      return { decoderAddress: label, decoderId: undefined }
    const [addressPart, indexPart] = label.split('-').map(Number)
    // A typed or pasted address that is not the row's own drops the decoder id
    // it no longer matches: the save prefers `decoderId` over the address, so
    // keeping it would write the old decoder. The save then resolves the id
    // from the address instead.
    const sameDecoder = label === String(row?.decoderAddress ?? '')
    if (sameDecoder) {
      return {
        decoderAddress: label,
        address: addressPart,
        index: indexPart,
        decoderId: (row as any)?.decoderId,
      }
    }
    const typed = typedDecoderOption([...optionByLabel.values()], label, row?.twiId)
    return {
      decoderAddress: label,
      address: addressPart,
      index: indexPart,
      twiId: typed?.twiId ?? null,
      decoderId: typed?.id || undefined,
    }
  }
}

export const typedDecoderOption = (
  options: any[],
  label: string,
  rowTwiId?: number | null,
) => {
  const matches = options.filter((option) => decoderRefOf(option) === label)
  if (matches.length === 1) return matches[0]
  return matches.find(
    (option) => rowTwiId != null && option.twiId === rowTwiId,
  )
}

const setupSubAreaEditor = (
  column: GridColumn<StationRow>,
  { areaOptionsData, subAreaOptionsData }: BuildColumnsParams,
) => {
  const subAreasForArea = (areaName: string) => {
    const target = String(areaName ?? '').trim()

    // Prefer the parent-area list (getAreaParent), whose entries carry their
    // nested sub-areas. `subAreaOptionsData` comes from a flat area list where
    // parents have no children attached.
    const parent = areaOptionsData.find(
      (area) => String(area?.name ?? '').trim() === target,
    )
    if (parent?.subAreas?.length) return parent.subAreas

    const legacy = subAreaOptionsData.find(
      (area) => String(area?.areaName ?? '').trim() === target,
    )
    return legacy?.subAreas ?? []
  }

  column.editorParams = ({ row }) => {
    const rawArea = row?.area
    const areaName = typeof rawArea === 'object' ? rawArea?.name : rawArea
    return {
      values: subAreasForArea(areaName).map((item: any) => item.name),
    }
  }

  column.valueSetter = (_row, value) => {
    let selected: any = null
    for (const area of [...areaOptionsData, ...subAreaOptionsData]) {
      const match = area.subAreas?.find((sub: any) => sub.name === value)
      if (match) {
        selected = match
        break
      }
    }
    return selected
      ? { subArea: selected.name, subAreaId: selected.id }
      : { subArea: value }
  }
}

const setupStationTypeEditor = (
  column: GridColumn<StationRow>,
  { t }: BuildColumnsParams,
) => {
  // The cell value is a STRING id (see normalize-station-data); `optionLabel`
  // shows the human label for it.
  column.editorParams = {
    values: STATION_TYPE_OPTIONS.map((item) => String(item.value)),
    optionLabel: (option: string) => {
      const found = STATION_TYPE_OPTIONS.find(
        (item) => String(item.value) === String(option),
      )
      return found ? t(found.label) : String(option)
    },
  }
}

// --- Renderers -------------------------------------------------------------

const setupRenderer = (
  column: GridColumn<StationRow>,
  config: any,
  { t, isDark, etZoneOptionsData, controlRenderers }: BuildColumnsParams,
) => {
  if (config.valueType === 'viewDetail' || config.valueType === 'action') return

  const control =
    controlRenderers?.[config.field as keyof typeof controlRenderers]
  if (control) {
    column.cellRenderer = control
    column.editable = false
    return
  }

  if (config.field === 'subArea') {
    column.valueGetter = subAreaName
    column.cellRenderer = PlainValueCell
    return
  }

  if (config.field === 'pipe') {
    column.valueGetter = pipeName
    column.cellRenderer = PlainValueCell
    return
  }

  if (config.field === 'etZone') {
    // The backend embeds the etZone with `name: null` (only the id is set), so
    // the label is resolved from the options list by id.
    column.valueGetter = (row) => etZoneNameFor(row, etZoneOptionsData)
    column.cellRenderer = PlainValueCell
    return
  }

  if (config.field === 'stationType') {
    column.cellRenderer = ({ value }) => {
      const found = STATION_TYPE_OPTIONS.find(
        (item) => String(item.value) === String(value),
      )
      if (!found) {
        return <span className={classes.mutedValue}>-</span>
      }
      // Irrigation → brand green chip; Non Irrigation → neutral slate chip.
      const isIrrigation = Number(found.value) === 2
      const chipColor = isIrrigation
        ? isDark
          ? '#5FE0AD'
          : '#004744'
        : isDark
          ? '#94A3B8'
          : '#64748B'
      return (
        <span
          className={classes.typeChip}
          style={{ '--chip-color': chipColor } as React.CSSProperties}
        >
          {t(found.label)}
        </span>
      )
    }
    return
  }

  if (config.valueType === 'status') {
    // Let the error text wrap fully and grow the row to fit instead of
    // truncating with an ellipsis.
    column.wrapText = true
    column.cellRenderer = ({ value, row }) => {
      const statusLower = String(value ?? '').toLowerCase()
      const errorCode = row?.errorCode
      const errorName = row?.errorName

      // Default: slate (Idle / Wait Stop); lighter variants in dark mode.
      let color = isDark ? '#94A3B8' : '#64748B'
      const isError = statusLower.includes('error')

      if (statusLower.includes('start') && statusLower.includes('wait')) {
        color = isDark ? '#FBBF24' : '#F59E0B'
      } else if (statusLower.includes('start') || statusLower.includes('running')) {
        color = isDark ? '#4ADE80' : '#16A34A'
      } else if (isError) {
        color = isDark ? '#F87171' : '#EF4444'
      }

      const errorInfo =
        isError && (errorCode || errorName)
          ? errorCode
            ? errorName
            ? t('stationsStatusErrorCodeWithName', {
                code: errorCode,
                name: errorName,
              })
            : t('stationsStatusErrorCode', { code: errorCode })
            : errorName || ''
          : ''

      const pill = (
        <span className={classes.statusPill}>{(value as string) || '-'}</span>
      )

      // The error rows are the rare ones; a Tooltip on every idle status cell
      // would mount popper machinery for a row that has nothing to explain.
      return (
        <div
          className={classes.statusCell}
          style={{ '--chip-color': color } as React.CSSProperties}
        >
          {errorInfo ? (
            <Tooltip title={errorInfo} arrow>
              {pill}
            </Tooltip>
          ) : (
            pill
          )}
          {errorInfo && <span className={classes.statusError}>{errorInfo}</span>}
        </div>
      )
    }
    return
  }

  column.cellRenderer = PlainValueCell
}

// --- Filters ---------------------------------------------------------------
// Dropdown columns get complete value lists from the option catalogs (loaded
// independently of the station rows), so the filter shows every possible value
// — not only those in the currently-loaded page.

const setupFilter = (
  column: GridColumn<StationRow>,
  config: any,
  params: BuildColumnsParams,
) => {
  const setFilter = (values: any[]) => {
    column.filter = 'set'
    column.filterParams = { values: dedupe(values) }
  }

  switch (config.field) {
    case 'area':
      setFilter(params.areaOptionsData.map((area: any) => area.name))
      break
    case 'subArea':
      setFilter([
        ...params.areaOptionsData.flatMap((area: any) =>
          (area.subAreas || []).map((sub: any) => sub.name),
        ),
        ...params.subAreaOptionsData.flatMap((area: any) =>
          (area.subAreas || []).map((sub: any) => sub.name),
        ),
      ])
      break
    case 'plant':
      setFilter(params.plantOptionsData.map((plant: any) => plant.description))
      break
    case 'soil':
      setFilter(params.soilOptionsData.map((soil: any) => soil.description))
      break
    case 'emitter':
      setFilter(params.emitterOptionsData.map((emitter: any) => emitter.emitter))
      break
    case 'pipe':
      setFilter(params.pipeOptionsData.map((pipe: any) => pipe.description))
      break
    case 'etZone':
      setFilter(params.etZoneOptionsData.map((zone: any) => zone.name))
      break
    case 'state':
      // Four groups rather than the nine states a station can hold — the same
      // four the footer counts. See station-state-buckets.ts.
      column.filter = StateBucketFilter
      break
    case 'stationType':
      column.filter = 'set'
      column.filterParams = {
        values: STATION_TYPE_OPTIONS.map((item) => String(item.value)),
        formatValue: (value: unknown) => {
          const found = STATION_TYPE_OPTIONS.find(
            (item) => String(item.value) === String(value),
          )
          return found ? params.t(found.label) : String(value)
        },
      }
      break
    case 'description':
    case 'decoderAddress':
      // Single "contains" search on the field's text.
      column.filter = TextContainsFilter
      break
    default:
      // Numeric and action columns carry no filter.
      break
  }
}
